import styled from '@emotion/styled';
import { TrackerActivationType } from '@racemap/sdk/schema/trackers';
import { getUserRoleForTracker } from '@racemap/utilities/functions/trackers';
import { validateMongoId } from '@racemap/utilities/functions/validation';
import type { FC } from 'react';
import {
  useCurrentUser,
  useInspectedTracker,
  useIsMobile,
  useQuery,
} from '../../../lib/customHooks';
import { type Entry, TabsNavigation } from '../../BasicComponents/Tabs/TabsNavigation';
import {
  IconCode,
  IconEmail,
  IconInfo,
  IconMap,
  IconQRCode,
  IconSIM,
  IconSetting,
  IconStart,
  getUserRoleIcon,
} from '../../Icon';
import { NoDataPlaceholder } from '../../Prediction/NoDataPlaceholder';
import { EventsTab } from '../InfoTabs/EventsTab';
import { InfosTab } from '../InfoTabs/InfosTab';
import { LocationTab } from '../InfoTabs/LocationTab';
import { MessagesTab } from '../InfoTabs/MessageTab';
import { QRCodeTab } from '../InfoTabs/QRCodeTab';
import { RawTab } from '../InfoTabs/RawTab';
import { SettingsTab } from '../InfoTabs/SettingsTab';
import { SimCardTab } from '../InfoTabs/SimCardTab';
import { UserTab } from '../InfoTabs/UserTab';

export const TrackerInfos: FC = () => {
  const query = useQuery();
  const inspectedTracker = useInspectedTracker();
  const currentUser = useCurrentUser();
  const isAdmin = !!currentUser?.admin;
  const isMobile = useIsMobile();

  if (
    inspectedTracker == null ||
    currentUser == null ||
    // fix for racecondition: after a history change that component updates
    // to early and redirect to '/admin/trackers/infos
    // the query gets the current value and prevent to render the tabs component
    // if no tracker was choosed
    !validateMongoId(query.get('inspectedTracker'))
  ) {
    return (
      <NoDataPlaceholder
        title="No Tracker inspected"
        subtitle="Choose a tracker per click on the left tables."
      />
    );
  }

  const userRole = getUserRoleForTracker(inspectedTracker, currentUser.id);
  const isArchived = inspectedTracker.activation.type === TrackerActivationType.ARCHIVED;

  const tabs: Array<Entry> = [
    {
      component: <InfosTab />,
      key: 'infos',
      label: (
        <div>
          <IconInfo /> Infos
        </div>
      ),
    },
    {
      component: <SettingsTab />,
      key: 'settings',
      label: (
        <div>
          <IconSetting /> Settings
        </div>
      ),
      disabled: isArchived,
    },
    {
      component: <UserTab />,
      key: 'userRoles',
      label: <div>{getUserRoleIcon(userRole, isAdmin)} User</div>,
    },
    {
      component: <EventsTab />,
      key: 'events',
      label: (
        <div>
          <IconStart /> Events
        </div>
      ),
    },
    {
      component: <MessagesTab />,
      key: 'messages',
      label: (
        <div style={{ whiteSpace: 'nowrap' }}>
          <IconEmail /> Messages
        </div>
      ),
      disabled: isArchived,
    },
  ];

  if (inspectedTracker.simCard != null) {
    tabs.push({
      component: <SimCardTab />,
      key: 'sim',
      label: (
        <div>
          <IconSIM /> SIM
        </div>
      ),
    });
  }

  if (isMobile) {
    tabs.push(
      ...[
        {
          component: <LocationTab />,
          key: 'location',
          label: (
            <div>
              <IconMap /> Location
            </div>
          ),
        },
      ],
    );
  }

  if (isAdmin) {
    tabs.push(
      ...[
        {
          component: <RawTab />,
          key: 'raw',
          label: (
            <div>
              <IconCode /> Raw
            </div>
          ),
        },
        {
          component: <QRCodeTab />,
          key: 'qrcode',
          label: (
            <div>
              <IconQRCode /> QR-Code
            </div>
          ),
        },
      ],
    );
  }

  return (
    <Container>
      <TabsNavigation entries={tabs} />
    </Container>
  );
};

const Container = styled.div`
  height: 100%;

  .nav-item,
  .nav {
    border-color: darkgray;
  }

  .nav-link {
    padding: 0.2rem 0.5rem;
  }

  // prevent the dragging of the icons
  .svg-inline--fa {
    overflow: hidden !important;
  }
`;
